import { SubDataList } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Tables";
import { GridColDef } from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import debounce from "lodash/debounce";
import * as React from "react";
import { useRecoilState } from "recoil";
import { makeGridState, TableStateExtended, useRegisterGridApiRef } from "../gridStatePersist";
import { BaseRow, SubDataListFactoryParams, SubDataListWrapperProps } from "../types";

export function SubDataListFactory<R extends BaseRow, E extends keyof EntityMappings>({
  apiRef,
  tableMode = "server",
  persistance,
  tableId,
  persistDelay = 1000,
}: SubDataListFactoryParams) {
  const SubDataListWrapper: React.FC<SubDataListWrapperProps<R, E>> = ({
    columns,
    showToolbar = true,
    dataSourceCache = null,
    ...props
  }) => {
    const serverSideLogic = tableMode === "server";
    const tableState = makeGridState(tableId, persistance);

    const [persistedTableState, setTableState] = useRecoilState(tableState);
    useRegisterGridApiRef(tableId, apiRef);

    const persistState = React.useMemo(
      () =>
        debounce((updates: Partial<TableStateExtended>) => {
          if (persistance) {
            setTableState((prevState) => ({
              ...prevState,
              ...updates,
            }));
          }
        }, persistDelay),
      [persistance, setTableState, persistDelay]
    );

    const initState = persistedTableState ?? props?.initialState;

    return (
      // @ts-ignore
      <SubDataList
        {...props}
        dataSourceCache={dataSourceCache}
        initialState={initState}
        apiRef={apiRef}
        processRowUpdate={(newV, oldV) => {
          return newV;
        }}
        showToolbar={showToolbar}
        filterMode={tableMode}
        sortingMode={tableMode}
        onFilterModelChange={(filterModel, details) => {
          persistState({
            filter: {
              ...initState?.filter,
              filterModel,
            },
          });
          if (props.onFilterModelChange) props.onFilterModelChange(filterModel, details);
        }}
        onSortModelChange={(sortModel, details) => {
          persistState({
            sorting: {
              ...initState?.sorting,
              sortModel,
            },
          });
          if (props.onSortModelChange) props.onSortModelChange(sortModel, details);
        }}
        pagination={false}
        columns={columns as GridColDef[]}
      />
    );
  };

  return SubDataListWrapper;
}
